import { FinancialReportEntity } from "domain/entity/FinancialReport/FinancialReportEntity";
import _ from "lodash";
import { FinancialReportConstant } from "presentation/constant/FinancialReport/FinancialReportConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useFinancialReportVM } from "presentation/hook/FinancialReport/useFinancialReportVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useFinancialReportTracked } from "presentation/store/FinancialReport/FinancialReportProvider";
import { processReportMissingFields, reportCheckFieldMandatory } from "presentation/utils/report/ReportCheckFieldUtils";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FieldType, HPHButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const FinancialReportPanel = () => {
    const FINANCIAL_REPORT_CONSTANT = FinancialReportConstant.Table;
    const [financialReportState] = useFinancialReportTracked();
    const financialReportVM = useFinancialReportVM();
    const { currentReport, reportIdTypeMap } = financialReportState;
    const messageBarVM = useMessageBarVM();
    const [isLoading, setIsLoading] = useState(false);
    const [allFormState, setAllFormState] = useState<{ [key: string]: string }>({});
    const [isInitPanelData, setIsInitPanelData] = useState(true)
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowRead } = anaInfoState;

    const onReportTypeChange = useCallback((fieldKey: string, fieldValue: IFieldValue) => {
        setAllFormState({});

        financialReportVM.onReportTypeChanged(fieldKey, fieldValue);
    }, [financialReportVM]);

    useEffect(() => {
        if (isInitPanelData) {
            setIsInitPanelData(false);
            onReportTypeChange("reportType", currentReport.reportType)
        }
    }, [currentReport.reportType, isInitPanelData, onReportTypeChange])

    useEffect(() => {
        if (currentReport.reportType) {
            const newFormState = processReportMissingFields(currentReport)

            setAllFormState(prev => ({
                ...prev,
                ...newFormState
            }));
        }
    }, [currentReport])

    const handleReset = useCallback(() => {
        setAllFormState({});
        financialReportVM.onResetClick();
    }, [financialReportVM]);

    const checkFieldMandatory = useCallback((currentReport: FinancialReportEntity) => {
        let ret = reportCheckFieldMandatory(currentReport, operatingUnit);

        if (!currentReport.reportType) {
            ret = { ...ret, reportType: "Missing", /*mandatoryCheckFail: "Please input the missing value."*/ };
        }
        if (!_.isEmpty(ret)) {
            setAllFormState(ret);
            messageBarVM.showError(ret["mandatoryCheckFail"] || ret["validateFailed"]);
            return true;
        }
        return false;
    }, [messageBarVM, operatingUnit]);

    const handlePrint = useCallback(() => {
        if (checkFieldMandatory(currentReport)) return;
        setIsLoading(true);
        financialReportVM.onPrintClick(reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Report is sent to printers successfully.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Report print failed.");
            }
        });
    }, [checkFieldMandatory, currentReport, financialReportVM, reportIdTypeMap, messageBarVM]);

    const handleExport = useCallback(() => {
        if (checkFieldMandatory(currentReport)) return;
        setIsLoading(true);
        financialReportVM.onExportClick(reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Export successfully.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Export failure.");
            }
        });
    }, [checkFieldMandatory, currentReport, financialReportVM, reportIdTypeMap, messageBarVM]);

    const handleDownloadClick = useCallback((exportType: string) => {
        if (checkFieldMandatory(currentReport)) return;
        setIsLoading(true);
        financialReportVM.onDownloadClick(reportIdTypeMap, currentReport, exportType).then(data => {
            if (data === "success") {
                messageBarVM.showSuccess("Download successfully.");
                setIsLoading(false);
            } else {
                messageBarVM.showError(data);
                setIsLoading(false);
            }
        });
    }, [checkFieldMandatory, currentReport, financialReportVM, messageBarVM, reportIdTypeMap]);

    const handleExportClick = useCallback((exportType: string) => {
        if (checkFieldMandatory(currentReport)) return;
        setIsLoading(true);
        financialReportVM.onExportAsClick(reportIdTypeMap, currentReport, exportType).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Export successfully.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Export failure.");
            }
        });
    }, [checkFieldMandatory, currentReport, financialReportVM, messageBarVM, reportIdTypeMap]);

    const handleSubmit = useCallback(() => {
        if (checkFieldMandatory(currentReport)) return;
        setIsLoading(true);
        financialReportVM.onSubmitClick(reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("The simulation document generation is submitted to the server.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Failed to execute Simulation Document Generation Preprocess.");
            }
        });
    }, [checkFieldMandatory, currentReport, financialReportVM, reportIdTypeMap, messageBarVM]);

    const memoDownloadMenu = useMemo(() => {
        return [
            {
                title: FinancialReportConstant.Title.CSV,
                disabled: false,
                command: () => {
                    handleDownloadClick(FinancialReportConstant.Title.CSV);
                }
            }/*,
            {
                title: FinancialReportConstant.Title.XLS,
                disabled: false,
                command: () => {
                    handleDownloadClick(FinancialReportConstant.Title.XLS);
                }
            }*/
        ]
    }, [handleDownloadClick])

    const memoExportMenu = useMemo(() => {
        return [
            {
                title: FinancialReportConstant.Title.PDF,
                disabled: false,
                command: () => {
                    handleExportClick(FinancialReportConstant.Title.PDF);
                }
            },
            {
                title: FinancialReportConstant.Title.CSV,
                disabled: false,
                command: () => {
                    handleExportClick(FinancialReportConstant.Title.CSV);
                }
            },
            {
                title: FinancialReportConstant.Title.RTF,
                disabled: false,
                command: () => {
                    handleExportClick(FinancialReportConstant.Title.RTF);
                }
            }
        ]
    }, [handleExportClick])

    const memoReportType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "630px", marginBottom: "20px", marginTop: "10px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["reportType"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.reportType ?? ''}
                fieldValue={currentReport.reportType}
                fieldLabel={FINANCIAL_REPORT_CONSTANT.REPORT_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'reportType'}
                maxLength={60}
                options={financialReportState.dynamicOptions.reportTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    onReportTypeChange(fieldKey, fieldValue && fieldValue.toString())} />
        </div>
        , [allFormState, currentReport.reportType, FINANCIAL_REPORT_CONSTANT.REPORT_TYPE, financialReportState.dynamicOptions.reportTypeDropdownOptions, onReportTypeChange])

    const memoPrinter = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["printer"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.printer ?? ''}
                fieldValue={currentReport.printer}
                fieldLabel={FINANCIAL_REPORT_CONSTANT.PRINTER}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'printer'}
                maxLength={60}
                options={financialReportState.dynamicOptions.printQueueDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    financialReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.printer, FINANCIAL_REPORT_CONSTANT.PRINTER, financialReportState.dynamicOptions.printQueueDropdownOptions, financialReportVM])

    const memoFileLocation = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["fileLocation"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.fileLocation ?? ''}
                fieldValue={currentReport.fileLocation}
                fieldLabel={FINANCIAL_REPORT_CONSTANT.FILE_LOCATION}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'fileLocation'}
                maxLength={60}
                options={financialReportState.dynamicOptions.fileLocationDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    financialReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.fileLocation, FINANCIAL_REPORT_CONSTANT.FILE_LOCATION, financialReportState.dynamicOptions.fileLocationDropdownOptions, financialReportVM])

    const memoSoa = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.soa ?? ''}
                fieldValue={currentReport?.soa}
                fieldLabel={FINANCIAL_REPORT_CONSTANT.SOA}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'soa'}
                maxLength={60}
                options={financialReportState.dynamicOptions.soaDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    financialReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.soa, FINANCIAL_REPORT_CONSTANT.SOA, financialReportState.dynamicOptions.soaDropdownOptions, financialReportVM])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.billToCompany ?? ''}
                fieldValue={currentReport?.billToCompany}
                fieldLabel={FINANCIAL_REPORT_CONSTANT.BILL_TO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={financialReportState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    financialReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.billToCompany, FINANCIAL_REPORT_CONSTANT.BILL_TO_COMPANY, financialReportState.dynamicOptions.companyCodeDropdownOptions, financialReportVM])


    const memoChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["chargeOnCompany"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.chargeOnCompany ?? ''}
                fieldValue={currentReport?.chargeOnCompany}
                fieldLabel={FINANCIAL_REPORT_CONSTANT.CHARGE_ON_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeOnCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={financialReportState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    financialReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.chargeOnCompany, FINANCIAL_REPORT_CONSTANT.CHARGE_ON_COMPANY, financialReportState.dynamicOptions.companyCodeDropdownOptions, financialReportVM])

    const memoDocDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={FINANCIAL_REPORT_CONSTANT.DOC_DATE_FROM}
                width="300px"
                date={currentReport?.docDateFrom}
                fieldName="docDateFrom"
                errorMessage={""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.DOC_DATE_FROM, currentReport?.docDateFrom, financialReportVM.onHeaderFieldChange])

    const memoDocDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={FINANCIAL_REPORT_CONSTANT.DOC_DATE_TO}
                width="300px"
                date={currentReport?.docDateTo}
                fieldName="docDateTo"
                errorMessage={""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.DOC_DATE_TO, currentReport?.docDateTo, financialReportVM.onHeaderFieldChange])

    const memoGlDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={FINANCIAL_REPORT_CONSTANT.GL_DATE_FROM}
                width="300px"
                date={currentReport?.glDateFrom}
                fieldName="glDateFrom"
                errorMessage={""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.GL_DATE_FROM, currentReport?.glDateFrom, financialReportVM.onHeaderFieldChange])

    const memoGlDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={FINANCIAL_REPORT_CONSTANT.GL_DATE_TO}
                width="300px"
                date={currentReport?.glDateTo}
                fieldName="glDateTo"
                errorMessage={""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.GL_DATE_TO, currentReport?.glDateTo, financialReportVM.onHeaderFieldChange])


    const memoEffectiveDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.EFFECTIVE_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.effectiveDateFrom}
                fieldName="effectiveDateFrom"
                errorMessage={allFormState ? allFormState["effectiveDateFrom"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.EFFECTIVE_DATE_FROM, allFormState, currentReport?.effectiveDateFrom, financialReportVM.onHeaderFieldChange])

    const memoEffectiveDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.EFFECTIVE_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.effectiveDateTo}
                fieldName="effectiveDateTo"
                errorMessage={allFormState ? allFormState["effectiveDateTo"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.EFFECTIVE_DATE_TO, allFormState, currentReport?.effectiveDateTo, financialReportVM.onHeaderFieldChange])

    const memoP1OperationDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.P1_OPERATION_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.p1OperationDateFrom}
                fieldName="p1OperationDateFrom"
                errorMessage={allFormState ? allFormState["p1OperationDateFrom"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.P1_OPERATION_DATE_FROM, allFormState, currentReport?.p1OperationDateFrom, financialReportVM.onHeaderFieldChange])

    const memoP1OperationDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.P1_OPERATION_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.p1OperationDateTo}
                fieldName="p1OperationDateTo"
                errorMessage={allFormState ? allFormState["p1OperationDateTo"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.P1_OPERATION_DATE_TO, allFormState, currentReport?.p1OperationDateTo, financialReportVM.onHeaderFieldChange])

    const memoP2OperationDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.P2_OPERATION_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.p2OperationDateFrom}
                fieldName="p2OperationDateFrom"
                errorMessage={allFormState ? allFormState["p2OperationDateFrom"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.P2_OPERATION_DATE_FROM, allFormState, currentReport?.p2OperationDateFrom, financialReportVM.onHeaderFieldChange])

    const memoP2OperationDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.P2_OPERATION_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.p2OperationDateTo}
                fieldName="p2OperationDateTo"
                errorMessage={allFormState ? allFormState["p2OperationDateTo"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.P2_OPERATION_DATE_TO, allFormState, currentReport?.p2OperationDateTo, financialReportVM.onHeaderFieldChange])

    const memoOperationDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.OPERATION_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.operationDateFrom}
                fieldName="operationDateFrom"
                errorMessage={allFormState ? allFormState["operationDateFrom"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.OPERATION_DATE_FROM, allFormState, currentReport?.operationDateFrom, financialReportVM.onHeaderFieldChange])

    const memoOperationDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.OPERATION_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.operationDateTo}
                fieldName="operationDateTo"
                errorMessage={allFormState ? allFormState["operationDateTo"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.OPERATION_DATE_TO, allFormState, currentReport?.operationDateTo, financialReportVM.onHeaderFieldChange])

    const memoEtdDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.ETD_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.etdFrom}
                fieldName="etdFrom"
                errorMessage={allFormState ? allFormState["etdFrom"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.ETD_DATE_FROM, allFormState, currentReport?.etdFrom, financialReportVM.onHeaderFieldChange])

    const memoEtdDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.ETD_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.etdTo}
                fieldName="etdTo"
                errorMessage={allFormState ? allFormState["etdTo"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.ETD_DATE_TO, allFormState, currentReport?.etdTo, financialReportVM.onHeaderFieldChange])

    const memoVslOperationDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.VSL_OPERATION_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.vslOperationDateFrom}
                fieldName="vslOperationDateFrom"
                errorMessage={allFormState ? allFormState["vslOperationDateFrom"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.VSL_OPERATION_DATE_FROM, allFormState, currentReport?.vslOperationDateFrom, financialReportVM.onHeaderFieldChange])

    const memoVslOperationDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.VSL_OPERATION_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.vslOperationDateTo}
                fieldName="vslOperationDateTo"
                errorMessage={allFormState ? allFormState["vslOperationDateTo"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.VSL_OPERATION_DATE_TO, allFormState, currentReport?.vslOperationDateTo, financialReportVM.onHeaderFieldChange])

    const memoSimulationDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={FINANCIAL_REPORT_CONSTANT.SIMULATION_DATE}
                width="300px"
                optional={false}
                date={currentReport?.simulationDate}
                fieldName="simulationDate"
                errorMessage={allFormState ? allFormState["simulationDate"] : ""}
                onDateChange={financialReportVM.onHeaderFieldChange} />
        </div>
        , [FINANCIAL_REPORT_CONSTANT.SIMULATION_DATE, allFormState, currentReport?.simulationDate, financialReportVM.onHeaderFieldChange])

    return <>
        <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
            <HeaderTitle>{FinancialReportConstant.Title.TITLE}</HeaderTitle>
            {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
            <StyledAction className="tm-animated-wrapper" style={{ marginRight: '5px' }}>
                <HPHButton label={WorkspaceConstant.Common.BUTTON_CLEAR} size={"Small"} theme={"Secondary"} disabled={false} onClick={handleReset} />
                {allowRead && <><div className="add-seperator" />
                    {(currentReport.reportType === "Storage and Reefer Charge Simulation Report")
                        && <HPHButton label={WorkspaceConstant.Common.BUTTON_SUBMIT} size={"Small"} theme={"Primary"} disabled={false} onClick={handleSubmit} />
                    }
                    {(currentReport.reportType !== "Storage and Reefer Charge Simulation Report") &&
                        <>
                            {(currentReport.reportType !== "Revenue By Tariff And Customer") && <HPHButton label={WorkspaceConstant.Common.BUTTON_EXPORT_AS} size={"Small"} theme={"Secondary"} disabled={false} onClick={handleExport} />}
                            {(currentReport.reportType === "Revenue By Tariff And Customer") && <OverflowMenuButton buttonTheme={"Secondary"} disabled={false} buttonLabel={WorkspaceConstant.Common.BUTTON_EXPORT_AS} navigationList={memoExportMenu} />}
                            <OverflowMenuButton buttonTheme={(currentReport.reportType === "Revenue By Tariff And Customer") ? "Secondary" : "Primary"} disabled={false} buttonLabel={WorkspaceConstant.Common.BUTTON_DOWNLOAD_AS} navigationList={memoDownloadMenu} />
                            {(currentReport.reportType === "Revenue By Tariff And Customer") && <HPHButton label={WorkspaceConstant.Common.BUTTON_PRINT} size={"Small"} theme={"Primary"} disabled={false} onClick={handlePrint} />}
                        </>
                    }
                </>}
            </StyledAction>
        </Sidebarheader>

        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoReportType}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoFileLocation}
                            {financialReportState.enabledField.printer ? memoPrinter : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.docDate ? memoDocDateFrom : null}
                            {financialReportState.enabledField.docDate ? memoDocDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.glDate ? memoGlDateFrom : null}
                            {financialReportState.enabledField.glDate ? memoGlDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.soa ? memoSoa : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.vslOperationDate ? memoVslOperationDateFrom : null}
                            {financialReportState.enabledField.vslOperationDate ? memoVslOperationDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.operationDate ? memoOperationDateFrom : null}
                            {financialReportState.enabledField.operationDate ? memoOperationDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.p1OperationDate ? memoP1OperationDateFrom : null}
                            {financialReportState.enabledField.p1OperationDate ? memoP1OperationDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.p2OperationDate ? memoP2OperationDateFrom : null}
                            {financialReportState.enabledField.p2OperationDate ? memoP2OperationDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.etd ? memoEtdDateFrom : null}
                            {financialReportState.enabledField.etd ? memoEtdDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.billToCompany ? memoBillToCompany : null}
                            {financialReportState.enabledField.chargeOnCompany ? memoChargeOnCompany : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.effectiveDate ? memoEffectiveDateFrom : null}
                            {financialReportState.enabledField.effectiveDate ? memoEffectiveDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {financialReportState.enabledField.simulationDate ? memoSimulationDate : null}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
        </StyledSidebar >
    </>
}

export default memo(FinancialReportPanel);
